//create axios instance
import axios from "axios";
import cookieCutter from "cookie-cutter";

const API = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  timeout: 30000, // 30 second timeout
});

// Flag to prevent multiple refresh attempts - scoped to instance
let isRefreshing = false;
let failedQueue = [];
const MAX_QUEUE_SIZE = 50; // Prevent memory leaks

const processQueue = (error, token = null) => {
  const queue = [...failedQueue]; // Create copy to avoid issues during processing
  failedQueue = []; // Clear immediately to prevent memory leaks
  
  queue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
};

// Request interceptor to add token to headers
API.interceptors.request.use(
  (config) => {
    const token = cookieCutter.get("accessToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh on 403 errors
API.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Check for 403 error with token expired message
    if (error.response?.status === 403 && 
        (error.response?.data?.detail === "Token has expired" ||
         error.response?.data?.code === "permission_denied") && 
        !originalRequest._retry) {
      
      if (isRefreshing) {
        // Prevent queue overflow
        if (failedQueue.length >= MAX_QUEUE_SIZE) {
          return Promise.reject(new Error("Too many concurrent requests"));
        }
        
        // If refresh is already in progress, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return API(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = cookieCutter.get("refreshToken");
        if (!refreshToken) {
          throw new Error("No refresh token available");
        }

        // Import AuthAPI dynamically to avoid circular dependency
        const AuthAPI = (await import("./authAxios")).default;
        
        const response = await AuthAPI.post("/api/auth/refresh-token", {
          refreshToken: refreshToken,
        });

        if (response.status === 200 && response.data.accessToken) {
          // Set new tokens with security flags
          cookieCutter.set("accessToken", response.data.accessToken, {
            path: "/",
            secure: true,
            sameSite: "strict",
          });

          if (response.data.refreshToken) {
            cookieCutter.set("refreshToken", response.data.refreshToken, {
              path: "/",
              secure: true,
              sameSite: "strict",
            });
          }
          
          // Process queued requests
          processQueue(null, response.data.accessToken);
          
          // Update the failed request with new token
          originalRequest.headers.Authorization = `Bearer ${response.data.accessToken}`;
          
          // Retry the original request
          return API(originalRequest);
        } else {
          throw new Error("Failed to refresh token");
        }
      } catch (refreshError) {
        // Refresh failed, clear tokens and redirect to login
        processQueue(refreshError, null);
        cookieCutter.set("accessToken", "", { expires: new Date(0) });
        cookieCutter.set("refreshToken", "", { expires: new Date(0) });
        
        // Redirect to login or handle logout
        window.location.href = process.env.REACT_APP_REDIRECT_URL + "/login?redirect_to=%2Fcompany%2Finterviews%2Fasync-video";

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default API;

